import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { app } from '../firebase';
import { getFirestore, doc, getDoc, collection, getDocs, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { Card, Row, Col, Spinner, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faMapMarkerAlt, faClock, faCar, faMoneyBill } from '@fortawesome/free-solid-svg-icons';
import NavBar from '../Components/NavBar';
import LeftBar from '../Components/LeftBar';
import './Dash.css';
import axios from 'axios';

function OrderDetails() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [updating, setUpdating] = useState(false);
  const db = getFirestore(app);

  const getSafeClientName = (name) => {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    return trimmed || 'Dear client';
  };

  const normalizePhone = (phone) =>
    typeof phone === 'string' ? phone.trim() : '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const docRef = doc(db, 'Orders', id);
        const docSnap = await getDoc(docRef);
        let orderData = null;
        if (docSnap.exists()) {
          orderData = { id: docSnap.id, ...docSnap.data() };
          setOrder(orderData);
          setSelectedDriver(orderData.driverId || '');

          if (orderData.clientId) {
            const clientRef = doc(db, 'clients', orderData.clientId);
            const clientSnap = await getDoc(clientRef);
            if (clientSnap.exists()) {
              setClient({ id: clientSnap.id, ...clientSnap.data() });
            }
          }
        }

        const driversSnap = await getDocs(collection(db, 'drivers'));
        const driversList = [];
        driversSnap.forEach((d) => {
          driversList.push({ id: d.id, ...d.data() });
        });
        setDrivers(driversList);
      } catch (err) {
        console.error('Error fetching order:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [db, id]);

  const assignDriver = async () => {
    if (!selectedDriver || !order) return;
    if (selectedDriver === order.driverId) return;
    setUpdating(true);
    try {
      const orderRef = doc(db, 'Orders', id);
      const nextStatus = order.status === 'Pending' ? 'Pickup' : order.status;
      await updateDoc(orderRef, {
        driverId: selectedDriver,
        status: nextStatus,
      });

      setOrder((prev) => ({
        ...prev,
        driverId: selectedDriver,
        status: nextStatus,
      }));

      if (order.tripId) {
        const tripRef = doc(db, 'Trips', order.tripId);
        await updateDoc(tripRef, { driverId: selectedDriver });
      }

      const driver = drivers.find((d) => d.id === selectedDriver);

      // Send SMS only to client with tracking link
      const trackingLink = `https://dropoff-mtaani.web.app/track-order/${id}`;
      const verb = order.driverId ? 'changed to' : 'assigned to';
      const clientMessage = `Hi, your order to ${order.toLocation} has been ${verb} driver ${driver?.full_name || 'N/A'}. Track here: ${trackingLink}`;

      if (order.clientPhone || client?.userphone) {
        await axios.post(
          "https://us-central1-dropoff-mtaani.cloudfunctions.net/api/send-sms",
          { message: clientMessage, mobile: order.clientPhone || client?.userphone },
          { headers: { "Content-Type": "application/json" } }
        );
      }
    } catch (err) {
      console.error('Error assigning driver:', err);
    } finally {
      setUpdating(false);
    }
  };

  const startTrip = async () => {
    if (!order || !order.driverId) return;
    setUpdating(true);
    try {
      const tripRef = await addDoc(collection(db, 'Trips'), {
        tripName: `Consolidation Trip - ${order.toLocation}`,
        orderIds: [order.id],
        driverId: order.driverId,
        clientId: order.clientId || null,
        fromLocation: order.fromLocation,
        toLocation: order.toLocation,
        status: 'onJourney',
        createdAt: serverTimestamp(),
      });

      const orderRef = doc(db, 'Orders', order.id);
      await updateDoc(orderRef, {
        tripId: tripRef.id,
        status: 'onJourney',
      });

      setOrder((prev) => ({
        ...prev,
        tripId: tripRef.id,
        status: 'onJourney',
      }));

      const trackingLink = `https://dropoff-mtaani.web.app/track-order/${id}`;
      const clientMessage = `Hi, your order is now on the way to ${order.toLocation}. Track here: ${trackingLink}`;
      const senderName = getSafeClientName(client?.businessName);
      const recipientMessage = `Hi, your delivery from ${senderName} is on the way. Track here: ${trackingLink}`;

      if (order.clientPhone || client?.userphone) {
        await axios.post(
          "https://us-central1-dropoff-mtaani.cloudfunctions.net/api/send-sms",
          { message: clientMessage, mobile: order.clientPhone || client?.userphone },
          { headers: { "Content-Type": "application/json" } }
        );
      }

      if (order.toContact) {
        await axios.post(
          "https://us-central1-dropoff-mtaani.cloudfunctions.net/api/send-sms",
          { message: recipientMessage, mobile: order.toContact },
          { headers: { "Content-Type": "application/json" } }
        );
      }
    } catch (err) {
      console.error('Error starting trip:', err);
    } finally {
      setUpdating(false);
    }
  };

  const endTrip = async () => {
    if (!order || !order.tripId) return;
    setUpdating(true);
    try {
      const tripRef = doc(db, 'Trips', order.tripId);
      await updateDoc(tripRef, {
        status: 'Completed',
        paymentStatus: 'Unpaid',
        completedAt: serverTimestamp(),
      });

      const orderRef = doc(db, 'Orders', order.id);
      await updateDoc(orderRef, {
        status: 'Completed',
      });

      setOrder((prev) => ({
        ...prev,
        status: 'Completed',
      }));

      const requesterPhone = normalizePhone(order.clientPhone || client?.userphone);
      const pickupPhone = normalizePhone(order.fromContactPhone);
      const clientMessage = `Hi, your order to ${order.toLocation} has been completed.`;
      const pickupMessage = `Hi, the order from ${order.fromLocation} to ${order.toLocation} has been completed.`;

      if (requesterPhone) {
        await axios.post(
          "https://us-central1-dropoff-mtaani.cloudfunctions.net/api/send-sms",
          { message: clientMessage, mobile: requesterPhone },
          { headers: { "Content-Type": "application/json" } }
        );
      }

      if (pickupPhone && pickupPhone !== requesterPhone) {
        await axios.post(
          "https://us-central1-dropoff-mtaani.cloudfunctions.net/api/send-sms",
          { message: pickupMessage, mobile: pickupPhone },
          { headers: { "Content-Type": "application/json" } }
        );
      }
    } catch (err) {
      console.error('Error ending trip:', err);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-5 text-center">
        <h5>Order not found</h5>
        <Link to="/orders" className="btn btn-primary mt-3">
          Back to Orders
        </Link>
      </div>
    );
  }

  return (
    <div className="contentMain">
      <LeftBar />
      <div className="theRightOne">
        <div className="DashTop">
          <h1>Order Details</h1>
          <NavBar />
        </div>
        <div className="dataComp">
          <Row className="g-4">
            {/* Client Info */}
            <Col md={6}>
              <Card className="shadow-sm border-0 rounded-4">
                <Card.Body>
                  <h5>Client</h5>
                  <p><strong>Name:</strong> {client?.full_name || order.newClientName || order.clientName || 'N/A'}</p>
                  <p><strong>Phone:</strong> {client?.userphone || order.clientPhone || 'N/A'}</p>
                  <p><strong>Business:</strong> {client?.businessName || 'N/A'}</p>
                </Card.Body>
              </Card>
            </Col>

            {/* Order Info */}
            <Col md={6}>
              <Card className="shadow-sm border-0 rounded-4">
                <Card.Body>
                  <h5>Order</h5>
                  <p>
                    <strong>Status:</strong>{' '}
                    <span
                      style={{
                        color:
                          order.status === 'Pending'
                            ? 'red'
                            : order.status === 'onJourney'
                            ? 'royalblue'
                            : order.status === 'Pickup'
                            ? 'orange'
                            : order.status === 'Picked'
                            ? 'purple'
                            : 'green',
                      }}
                    >
                      {order.status}
                    </span>
                  </p>
                  <p>
                    <FontAwesomeIcon icon={faClock} className="me-2" />{' '}
                    {order.timestamp?.toDate().toLocaleString()}
                  </p>

                  {/* Assign/Change Driver */}
                  <div className="mt-3">
                    <Form.Select
                      value={selectedDriver}
                      onChange={(e) => setSelectedDriver(e.target.value)}
                    >
                      <option value="">Select Driver</option>
                      {drivers.map((driver) => (
                        <option key={driver.id} value={driver.id}>
                          {driver.full_name || driver.name} - {driver.userphone}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      variant="primary"
                      className="mt-2"
                      disabled={!selectedDriver || updating || selectedDriver === order.driverId}
                      onClick={assignDriver}
                    >
                      {updating
                        ? order.driverId
                          ? 'Changing...'
                          : 'Assigning...'
                        : order.driverId
                        ? 'Change Driver'
                        : 'Assign Driver'}
                    </Button>
                  </div>

                  {/* Start Trip Button */}
                  {order.status === 'Pickup' && (
                    <Button
                      variant="success"
                      className="mt-3"
                      disabled={updating}
                      onClick={startTrip}
                    >
                      {updating ? 'Starting Trip...' : 'Start Trip'}
                    </Button>
                  )}

                  {/* End Trip Button */}
                  {order.status === 'onJourney' && (
                    <Button
                      variant="danger"
                      className="mt-3"
                      disabled={updating}
                      onClick={endTrip}
                    >
                      {updating ? 'Ending Trip...' : 'End Trip'}
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Pickup Details */}
            <Col md={6}>
              <Card className="shadow-sm border-0 rounded-4">
                <Card.Body>
                  <h5>Pickup Details</h5>
                  <p><FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-danger" /> {order.fromLocation}</p>
                  <p><strong>Details:</strong> {order.fromDetails || 'N/A'}</p>
                  <p><strong>Contact Person:</strong> {order.fromContact || 'N/A'}</p>
                  <p><strong>Phone:</strong> {order.fromContactPhone || 'N/A'}</p>
                </Card.Body>
              </Card>
            </Col>

            {/* Delivery Details */}
            <Col md={6}>
              <Card className="shadow-sm border-0 rounded-4">
                <Card.Body>
                  <h5>Delivery Details</h5>
                  <p><FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-success" /> {order.toLocation}</p>
                  <p><strong>Details:</strong> {order.toDetails || 'N/A'}</p>
                  <p><strong>Recipient Name:</strong> {order.toName || 'N/A'}</p>
                  <p><strong>Recipient Contact:</strong> {order.toContact || 'N/A'}</p>
                </Card.Body>
              </Card>
            </Col>

            {/* Instructions */}
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded-4">
                <Card.Body>
                  <h5>Delivery Instructions</h5>
                  <p>{order.deliveryInstructions || 'N/A'}</p>
                </Card.Body>
              </Card>
            </Col>

            {/* Amount */}
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded-4">
                <Card.Body>
                  <h5>Trip Summary</h5>
                  <p><FontAwesomeIcon icon={faMoneyBill} className="me-2 text-success" /> <strong>Amount:</strong> {order.amount ? `KES ${order.amount}` : 'N/A'}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}

export default OrderDetails;
